import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const box = { background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: '1.25rem', marginBottom: '1rem' };
const BRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '-');
const errText = (e, f) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : f);
const btn = (color) => ({ background: 'white', color, border: `1px solid ${color}`, borderRadius: 6, padding: '0.2rem 0.5rem', fontSize: '0.78rem', cursor: 'pointer', marginRight: 4 });
const STATUS = { approved: 'Pago', pending: 'Pendente', in_process: 'Em análise', rejected: 'Recusado', cancelled: 'Cancelado', refunded: 'Reembolsado',
  partially_refunded: 'Reembolso parcial', charged_back: 'Contestado', created: 'Criado', failed: 'Falhou', uncertain: 'Sem confirmação', expired: 'Vencido' };
const KIND = { card: 'Cartão', pix: 'Pix' };

const Status = () => {
  const [st, setSt] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/payments/status`, { headers: headers() }).then((r) => setSt(r.data)).catch(() => setSt({})); }, []);
  if (!st) return <Loader2 className="w-5 h-5 animate-spin" />;
  const ok = st.configured && st.webhook_token_configured;
  return (
    <div style={box} data-testid="payments-status">
      <h3 style={{ fontWeight: 700, color: '#2D3748', display: 'flex', gap: 8, alignItems: 'center' }}>
        {ok ? <CheckCircle2 className="w-5 h-5" style={{ color: '#38a169' }} /> : <AlertTriangle className="w-5 h-5" style={{ color: '#d69e2e' }} />}Asaas
      </h3>
      <p style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: 6 }}>
        Conta: <strong data-testid="payments-env">{!st.configured ? 'não configurada' : st.sandbox ? 'Sandbox (testes)' : 'Produção'}</strong> ·
        Webhook: <strong data-testid="payments-webhook">{st.webhook_token_configured ? 'configurado' : 'falta o token'}</strong>
      </p>
      <p style={{ fontSize: '0.8rem', color: '#718096', marginTop: 4 }}>
        Em aberto: {st.open_charges ?? 0} · Notificações em nova tentativa: {st.webhook_retrying ?? 0} · Falhas definitivas: {st.webhook_dead ?? 0} · Para revisar: {st.needs_review ?? 0}
      </p>
    </div>
  );
};

const ChargeRow = ({ c, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const pid = c.approved_payment_id || c.payment_ids?.[c.payment_ids.length - 1];
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); window.alert(ok); onChanged(); } catch (e) { window.alert(errText(e, 'Operação não concluída.')); } finally { setBusy(false); }
  };
  const refund = () => {
    const raw = window.prompt('Deixe vazio para reembolso total ou informe o valor parcial (ex.: 10,50). Só o reembolso total corta o acesso.', '');
    if (raw === null) return;
    const amount = raw.trim() ? Number(raw.replace(',', '.')) : null;
    if (raw.trim() && !(amount > 0)) { window.alert('Valor inválido.'); return; }
    if (window.prompt(`Confirma o reembolso ${amount ? `de ${BRL(amount)}` : 'TOTAL'}? Digite REEMBOLSAR.`) !== 'REEMBOLSAR') return;
    const requestId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/\./g, '');
    act(async () => {
      const { data } = await axios.post(`${API}/admin/payments/${pid}/refund`, { request_id: requestId, amount, confirm: 'REEMBOLSAR' }, { headers: headers() });
      if (data.status !== 'confirmed') throw { response: { data: { detail: 'Reembolso solicitado; aguardando confirmação do Asaas.' } } };
    }, 'Reembolso confirmado pelo Asaas.');
  };
  const cancel = () => window.confirm('Cancelar esta cobrança não paga?') && act(() => axios.post(`${API}/admin/payments/${pid}/cancel`, {}, { headers: headers() }), 'Cobrança cancelada.');
  const sync = () => act(() => axios.post(`${API}/admin/payments/${pid}/reconcile`, {}, { headers: headers() }), 'Situação atualizada.');
  return (
    <tr data-testid={`pay-charge-${c.id}`} style={{ borderTop: '1px solid #edf2f7' }}>
      <td>{fmt(c.created_at)}</td><td>{c.editora}</td><td>{c.leitor}</td><td>{KIND[c.kind] || c.kind} · {c.months}m</td>
      <td>{BRL(c.expected_amount)}{c.refund_recorded ? ` (−${BRL(c.refund_recorded)})` : ''}</td>
      <td data-testid={`pay-charge-status-${c.id}`}>{STATUS[c.status] || c.status}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : pid && <>
          {['approved', 'partially_refunded'].includes(c.status) && <button type="button" style={btn('#c53030')} onClick={refund} data-testid={`pay-refund-${c.id}`}>Reembolsar</button>}
          {['pending', 'in_process', 'expired'].includes(c.status) && <button type="button" style={btn('#975a16')} onClick={cancel} data-testid={`pay-cancel-${c.id}`}>Cancelar</button>}
          <button type="button" style={btn('#4a5568')} onClick={sync} data-testid={`pay-sync-${c.id}`}>Atualizar</button>
        </>}
      </td>
    </tr>
  );
};

const Charges = () => {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('');
  const load = () => axios.get(`${API}/admin/payments/charges`, { headers: headers(), params: status ? { status } : {} }).then((r) => setRows(r.data.charges)).catch(() => setRows([]));
  useEffect(() => { load(); }, [status]);
  return (
    <div style={box} data-testid="payments-charges">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ fontWeight: 700, color: '#2D3748' }}>Pagamentos, cancelamentos e reembolsos</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payments-filter" style={{ border: '1px solid #cbd5e0', borderRadius: 6, padding: '0.25rem' }}>
            <option value="">Todos</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button type="button" onClick={load} data-testid="payments-reload" style={btn('#4a5568')}><RefreshCw className="w-3 h-3" /></button>
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: '#718096', margin: '6px 0' }}>Reembolsos e contestações viram desconto no próximo repasse da editora (a editora devolve o valor integral).</p>
      {!rows ? <Loader2 className="w-5 h-5 animate-spin" /> : !rows.length ? <p style={{ fontSize: '0.85rem', color: '#718096' }}>Nenhum pagamento.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.8rem', color: '#2D3748', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left', color: '#718096' }}><th>Data</th><th>Editora</th><th>Leitor</th><th>Tipo</th><th>Valor</th><th>Situação</th><th /></tr></thead>
            <tbody>{rows.map((c) => <ChargeRow key={c.id} c={c} onChanged={load} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const AdminPayments = () => (<div data-testid="admin-payments"><Status /><Charges /></div>);
